import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { type QueryClient, type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

const contractorBadgePictureResponseSchema = z.object({
  data: z.object({
    type: z.literal("contractor-badge-picture"),
    id: z.string().min(1),
    attributes: z.object({
      // can be empty if the picture is not uploaded yet
      presignedUrl: z.string().optional(),
      workerId: z.string().min(1),
    }),
  }),
});

export type ContractorBadgePictureResponse = z.infer<typeof contractorBadgePictureResponseSchema>;

interface GetContractorBadgePicturesParams {
  workerId: string;
}

export function getContractorBadgePicturesUrl(): string {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/contractor-badge-pictures`;
}

export function resetContractorBadgePicturesQuery(queryClient: QueryClient) {
  void queryClient.resetQueries({
    queryKey: [getContractorBadgePicturesUrl()],
  });
}

export function useContractorBadgePictures(
  params: GetContractorBadgePicturesParams,
  options: UseGetQueryOptions<ContractorBadgePictureResponse> = {}
): UseQueryResult<ContractorBadgePictureResponse> {
  const { workerId } = params;

  return useGetQuery({
    url: getContractorBadgePicturesUrl(),
    queryParams: {
      filter: {
        workerId,
      },
    },
    responseSchema: contractorBadgePictureResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_FETCH_IMAGE_FAILURE,
    },
    enabled: Boolean(workerId),
    ...options,
  });
}
